












































import Vue from 'vue'
import Analytics from '@/services/Analytics'
import { mapGetters } from 'vuex'
import subscriptionMixin from '@/mixins/subscription-mixin.js'
import SubscriptionOption from '@/types/SubscriptionOption.interface'
export default Vue.extend({
    mixins: [subscriptionMixin],
    props: {
        //  Determines if we use paywall as a block
        block: {
            type: Boolean,
            default: true
        },
        //  Determines the text on our first button
        firstBtnTxt: {
            type: String,
            default: null
        },
        //  The word to replace "Unlock" with once active
        keyword: {
            type: String,
            default: 'Create'
        },
        //  Determines what module block we are doing
        metadata: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            content: {
                autoresponder_enabled: {
                    title: 'Unlock Keyword Autoresponders',
                    description:
                        'Build a subscriber list, automate customer support and create text message campaigns.',
                    bullets: [
                        'Use keywords like SUBSCRIBE or RSVP to sort contacts.',
                        'Run "Text-KEYWORD" campaigns for your business.',
                        'Automate customer service and feedback.'
                    ],
                    image: '/media/images/autoresponderPaywall.png',
                    learning_center:
                        'https://learn.messagedesk.com/en/articles/4662511-autoresponders-overview'
                },
                broadcast_enabled: {
                    title: 'Unlock Broadcasts and Mass  Messaging Without Reply All',
                    description:
                        'Start sending large-scale, personalized text message broadcasts to any group of contacts.',
                    bullets: [
                        'Works like email “BCC”, but for text messaging.',
                        'Create and filter groups of contacts based on any attribute.',
                        'Personalize SMS and MMS mass messages with tags.'
                    ],
                    image: '/media/images/BroadcastsPaywall.png',
                    learning_center:
                        'https://learn.messagedesk.com/en/articles/4544124-broadcasts-overview'
                },
                chatbot_enabled: {
                    title: 'Unlock A Website Chatbot to Start More Conversations',
                    description:
                        'Start more conversations with an embeddable, custom website chatbot integrated with web forms.',
                    bullets: [
                        'Embed a website chatbot on any website (Wordpress, Wix, etc.)',
                        'Ask questions with embedded forms and autoresponders',
                        'All conversations appear in your team inbox for easy response.'
                    ],
                    image: '/media/images/chatbotPaywall.png',
                    learning_center:
                        'https://learn.messagedesk.com/en/articles/5473493-chatbot-overview'
                },
                form_enabled: {
                    title: 'Unlock Custom, Embeddable Web Forms',
                    description:
                        'Collect and update info from contacts, customers and clients by texting links to custom web forms.',
                    bullets: [
                        ' Use web forms with website chatbot to start more conversations.',
                        'Automatically collect info from contacts, customers and clients.',
                        'Autorespond to form submissions with a custom text message.'
                    ],
                    image: '/media/images/formPaywall.png',
                    learning_center:
                        'https://learn.messagedesk.com/en/articles/5076369-contact-forms-overview'
                },
                group_enabled: {
                    title: 'Unlock Groups to Organize and Filter Contacts',
                    description:
                        'Create groups to send targeted messages to the right contacts at the right time.',
                    bullets: [
                        'Create groups based on any contact property.',
                        'Use custom fields with groups for specialized filtering.',
                        'Send and schedule broadcasts to groups of contacts.'
                    ],
                    image: '/media/images/groupPaywall.png'
                },
                office_hour_enabled: {
                    title: 'Unlock Automatic Out-of-Office Text Replies',
                    description:
                        "Set up 'office hours' to automatically send text messages and respond while you're away.",
                    bullets: [
                        'Manage expectations with automated text messages responses.',
                        'Smooth daily operations by automatically texting links to resources.',
                        'Fully customizable “office hours” for any sized operation.'
                    ],
                    image: '/media/images/Out-Office-Graphic.svg',
                    learning_center:
                        'https://learn.messagedesk.com/en/articles/4731997-office-hours-how-to-set-an-automatic-out-of-office-text-message'
                },
                payment_automation_enabled: {
                    title: 'Unlock Text-Based Payment Services and Integrations',
                    description:
                        'Use Text-to-Pay to get paid faster, schedule payment reminders and auto-answer account questions.',
                    bullets: [
                        'Reduce late payments and monthly accounts recievables.',
                        'Schedule payment reminder text mesages.',
                        'Integrate with QuickBooks Online, Xero and more.'
                    ],
                    image: '/media/images/paymentAutomationPaywall.png'
                },
                template_enabled: {
                    title: 'Unlock Time-Saving Message Templates',
                    description:
                        'Save time with pre-saved message templates for the messages you send all the time.',
                    bullets: [
                        'Pre-save the messages you send all the time as templatates.',
                        'Add tags and media to personalize messages at scale.',
                        'Standardize your messages across users and teams.'
                    ],
                    image: '/media/images/templatePaywall.png',
                    learning_center:
                        'https://learn.messagedesk.com/en/articles/4527011-how-to-create-a-text-message-template'
                }
            }
        }
    },
    mounted() {
        //  Embed script for calendly
        let embedCalendly = document.createElement('script')
        embedCalendly.setAttribute(
            'src',
            'https://assets.calendly.com/assets/external/widget.js'
        )
        embedCalendly.setAttribute('async', 'async')
        document.head.appendChild(embedCalendly)

        //  Embed calendly css stylesheet
        let calendlyCSS = document.createElement('link')
        calendlyCSS.setAttribute(
            'href',
            'https://assets.calendly.com/assets/external/widget.css'
        )
        calendlyCSS.setAttribute('rel', 'stylesheet')
        document.head.appendChild(calendlyCSS)
    },
    computed: {
        ...mapGetters(['authIsAdmin', 'subscription']),
        monthly(): boolean {
            return this.currentPlan?.interval === 'month'
        },
        nextPlan(): SubscriptionOption {
            if (this.monthly === true) return this.nextPlanMonthly

            if (this.monthly === false) return this.nextPlanAnnual

            return null
        },
        nextPlanMonthly(): SubscriptionOption {
            const monthOptions = this.$store.getters.monthOptions

            if (!monthOptions) return null

            const availableMonthly = monthOptions
                .filter(
                    (x) =>
                        x.metadata.is_active === 'true' &&
                        x.metadata.default_start_plan !== 'true'
                )
                .sort((a, b) => {
                    return a.metadata.index - b.metadata.index
                })

            const nextPlanMonthly = availableMonthly.find(
                (plan) => plan.metadata[this.metadata] !== 'false'
            )

            return nextPlanMonthly
        },
        nextPlanAnnual(): SubscriptionOption {
            const yearOptions = this.$store.getters.yearOptions

            if (!yearOptions) return null

            const availableAnnual = yearOptions
                .filter(
                    (x) =>
                        x.metadata.is_active === 'true' &&
                        x.metadata.default_start_plan !== 'true'
                )
                .sort((a, b) => {
                    return a.metadata.index - b.metadata.index
                })

            const nextPlanYearly = availableAnnual.find(
                (plan) => plan.metadata[this.metadata] !== 'false'
            )

            return nextPlanYearly
        },
        title(): string {
            //  Grab our title based on metadata
            const title = this.content[this.metadata].title

            //  Replace unlock with our keyword
            return title.replace('Unlock', this.keyword)
        }
    },
    methods: {
        firstButtonHandler(): void {
            //  If we are blocking then go to manage plan
            if (this.block) return this.managePlanClicked()

            //  Else, emit an event
            return this.$emit('create')
        },
        secondButtonHandler(): void {
            //  If we are blocking then talk to tales
            if (this.block) return this.talkToSalesClicked()

            //  Else, route to the learning center
            const link = this.content[this.metadata].learning_center

            //  Open the link
            window.open(link, this.$store.getters.isApp ? '_system' : '_blank')
        },
        async managePlanClicked(): Promise<void> {
            //  Naviagte to the subscription settings
            this.$router.push(`/billing/manage?index=${this.nextPlan.metadata.index}`)
        },
        talkToSalesClicked(): void {
            // @ts-ignore
            Calendly.initPopupWidget({
                url: 'https://calendly.com/d/cqdk-krr-g3n/messagedesk-account-meeting'
            })
        }
    }
})
